import React from 'react';
import { Heart, MessageCircle, Shield, Sparkles } from 'lucide-react';
import SimpleAudioButton from './audio/SimpleAudioButton';
import SmallAudioButton from './audio/SmallAudioButton';

const ProblemSolution: React.FC = () => {
  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-white to-[#B3D9E6]/10">
      <div className="container mx-auto px-4 md:px-6">
        {/* Problem Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3 font-['Poppins']">
            When Life Gets Overwhelming, <span className="text-[#F4B6A1]">Who Really Listens?</span>
          </h2>
          <div className="flex justify-center mb-6">
            <SimpleAudioButton audioSrc="/assets/audio/who-really-listens.mp3" />
          </div>
          <p className="text-lg text-gray-600 font-['Merriweather']">
            In today's fast-paced world, finding someone who truly hears you can feel impossible. 
            Friends are busy, therapists have limited hours, and sometimes you just need to be 
            understood right now, without judgment or advice.
          </p>
        </div>

        {/* Problem Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-20">
          <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
            <div className="bg-[#F4B6A1]/20 w-12 h-12 rounded-full flex items-center justify-center mb-4">
              <MessageCircle className="w-6 h-6 text-[#F4B6A1]" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-3">Limited Access to Support</h3>
            <p className="text-gray-600">
              Traditional therapy can be expensive, sometimes has long wait times, and isn't available 
              24/7 when emotional crises don't follow business hours.
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
            <div className="bg-[#C2A4D6]/20 w-12 h-12 rounded-full flex items-center justify-center mb-4">
              <Heart className="w-6 h-6 text-[#C2A4D6]" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-3">Fear of Judgment</h3>
            <p className="text-gray-600">
              Opening up to friends or family can come with unwanted advice, 
              judgment, or the burden of knowing you're affecting others.
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
            <div className="bg-[#B3D9E6]/20 w-12 h-12 rounded-full flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-[#B3D9E6]" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-3">Emotional Isolation</h3>
            <p className="text-gray-600">
              Sometimes we just need someone to listen and validate our feelings, 
              not fix our problems or tell us what we "should" do.
            </p>
          </div>
        </div>

        {/* Solution Section */}
        <div id="how-it-works" className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-block bg-[#A9D0A1]/20 px-4 py-2 rounded-full mb-4">
            <span className="text-[#A9D0A1] font-medium">Our Solution</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3 font-['Poppins']">
            Introducing <span className="text-[#C2A4D6]">Cora</span>
          </h2>
          <div className="flex justify-center mb-6">
            <SimpleAudioButton audioSrc="/assets/audio/introducing-cora.mp3" />
          </div>
          <p className="text-lg text-gray-600 font-['Merriweather']">
            An AI companion that combines advanced emotional intelligence with human-like 
            conversation to create a safe space where you feel genuinely understood and supported.
          </p>
        </div>

        {/* Solution Features */}
        <div className="grid md:grid-cols-2 gap-10">
          <div className="flex flex-col justify-center">
            <ul className="space-y-6">
              <li className="flex gap-4">
                <div className="bg-[#C2A4D6]/20 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Sparkles className="w-6 h-6 text-[#C2A4D6]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2 inline-flex items-center gap-1">
                    Always Available
                    <SmallAudioButton audioSrc="/assets/audio/always-available.mp3" />
                  </h3>
                  <p className="text-gray-600">
                    24/7 support whenever you need it, whether it's 3 AM or during your lunch break.
                    No appointments needed.
                  </p>
                </div>
              </li>
              
              <li className="flex gap-4">
                <div className="bg-[#B3D9E6]/20 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <MessageCircle className="w-6 h-6 text-[#B3D9E6]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2 inline-flex items-center gap-1">
                    Judgment-Free Zone
                    <SmallAudioButton audioSrc="/assets/audio/judgement-free-zone.mp3" />
                  </h3>
                  <p className="text-gray-600">
                    Share your thoughts freely without fear of criticism, unsolicited advice,
                    or social repercussions.
                  </p>
                </div>
              </li>
              
              <li className="flex gap-4">
                <div className="bg-[#F4B6A1]/20 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Heart className="w-6 h-6 text-[#F4B6A1]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2 inline-flex items-center gap-1">
                    Personalized Support
                    <SmallAudioButton audioSrc="/assets/audio/personalized-approach.mp3" />
                  </h3>
                  <p className="text-gray-600">
                    Our AI learns your communication style and emotional patterns to provide
                    increasingly relevant and personalized responses.
                  </p>
                </div>
              </li>
              
              <li className="flex gap-4">
                <div className="bg-[#A9D0A1]/20 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Shield className="w-6 h-6 text-[#A9D0A1]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2 inline-flex items-center gap-1">
                    Complete Privacy
                    <SmallAudioButton audioSrc="/assets/audio/complete-privacy.mp3" />
                  </h3>
                  <p className="text-gray-600">
                    Your conversations are private and secure. We use state-of-the-art encryption
                    and never share your personal information.
                  </p>
                </div>
              </li>
            </ul>
          </div>
          
          <div className="relative order-first md:order-last mb-8 md:mb-0">
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-[#F4B6A1]/20 rounded-full"></div>
            <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-[#C2A4D6]/20 rounded-full"></div>
            
            <img 
              src="https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/blogs/2147510132/images/3c80444-0844-e5a7-5654-52b7800a51f_lemski._Silhouette_of_two_faces_profile_one_intuitive_and_one_l_d2e40d25-31b8-4ed1-96d6-63b1c7b223f6.png" 
              alt="Two faces in profile showing emotional connection" 
              className="w-full h-full object-cover rounded-2xl shadow-lg relative z-10"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSolution;